/* eslint-disable max-lines */
import { Injectable, inject } from '@angular/core';
import { Action, createSelector, State, type StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { type Control, DynamicFlatNode, type IChannel, TreeEntityType } from 'commons';
import { produce } from 'immer';
import { isSameControlAs } from '../../../components/dtis-multilane-viewer/multilane.utils';
import { findbyId as byId } from '../../../state_utils';
import { db } from '../../db';
import { AddBatchControlsToCurrentView, RemoveControlFromCurrentView } from '../multi-view/actions';
import { MultiViewState } from '../multi-view/multi-view.state';
import { getDefaults } from './getDefaults';
import { TreeStateActions } from './tree.actions';
import type { TreeStateModel } from './tree.model';

@State<TreeStateModel>({
  name: 'tree_state',
  defaults: getDefaults(),
})
@Injectable()
export class TreeState {
  /**
   * Injected store instance.
   */
  private readonly store = inject(Store);

  private static findNodeById(state: TreeStateModel, id: string) {
    return state.flatTree.find((n) => n.id === id);
  }

  private static findParentNode(state: TreeStateModel, node?: DynamicFlatNode) {
    return state.flatTree.find((d) => d.id === node?.entity?.parentIdentifier);
  }

  /**
   * Selector to get a node by its ID.
   * @param id - The ID of the node.
   * @returns A selector function.
   */
  static getNodeFromId(id: string) {
    return createSelector([TreeState], (state: TreeStateModel) => {
      return TreeState.findNodeById(state, id);
    });
  }

  /**
   * Selector to get the breadcrumb path for a node.
   * @param node - The node to get the breadcrumb for.
   * @returns A selector function.
   */
  static getBreadcrump(node: DynamicFlatNode) {
    return createSelector([TreeState], (state: TreeStateModel) => {
      const origin = TreeState.findNodeById(state, node.id);

      const path: any = {};
      if (!origin) {
        return {};
      }
      if (origin.type === TreeEntityType.CHANNEL) {
        path.channel = origin;
        path.configuration = TreeState.findParentNode(state, path.channel);
        path.instance = TreeState.findParentNode(state, path.configuration);
        path.master = TreeState.findParentNode(state, path.instance);
        path.root = TreeState.findParentNode(state, path.master);
      }
      if (origin.type === TreeEntityType.CHANNEL_GROUP) {
        path.instance = TreeState.findParentNode(state, origin);
        path.master = TreeState.findParentNode(state, path.instance);
        path.root = TreeState.findParentNode(state, path.master);
      }

      if (origin.type === TreeEntityType.INSTANCE) {
        path.configuration = TreeState.findParentNode(state, origin);
        path.instance = TreeState.findParentNode(state, path.configuration);
      }
      if (origin.type === TreeEntityType.MASTER) {
        path.configuration = TreeState.findParentNode(state, origin);
        path.instance = TreeState.findParentNode(state, path.configuration);
        path.master = TreeState.findParentNode(state, path.instance);
      }
      return path;
    });
  }

  /**
   * Selector to get root nodes.
   */
  static readonly getRoots = createSelector([TreeState], (state: TreeStateModel) => {
    return state?.flatTree?.filter(({ type }) => type === TreeEntityType.USE_CASE_ROOT) ?? [];
  });

  /**
   * Selector to get master nodes.
   */
  static readonly getMasters = createSelector([TreeState], (state: TreeStateModel) => state.flatTree?.filter((c) => c.type === TreeEntityType.MASTER) ?? []);

  /**
   * Selector to check if the tree is loading.
   */
  static readonly isLoading = createSelector([TreeState], (state: TreeStateModel) => state.isLoading);

  /**
   * Selector to get the flat tree.
   */
  static readonly getFlatTree = createSelector([TreeState], (state: TreeStateModel) => state?.flatTree.map((d) => new DynamicFlatNode(d)) ?? []);

  /**
   * Selector to get group nodes.
   */
  static readonly getGroups = createSelector(
    [TreeState],
    (state: TreeStateModel) => state.flatTree?.filter((c) => c.type === TreeEntityType.CHANNEL_GROUP) ?? []
  );

  /**
   * Selector to get instance nodes.
   */
  static readonly getInstances = createSelector(
    [TreeState],
    (state: TreeStateModel) => state.flatTree?.filter((c) => c.type === TreeEntityType.INSTANCE) ?? []
  );

  /**
   * Selector to get channel nodes.
   */
  static readonly getChannels = createSelector([TreeState], (state: TreeStateModel) =>
    state.flatTree?.filter((c) => c.type === TreeEntityType.CHANNEL).map((c) => new DynamicFlatNode(c) as DynamicFlatNode<IChannel>)
  );

  /**
   * Selector to get multi-selection state.
   */
  static readonly multiSelection = createSelector([TreeState], (state: TreeStateModel) => state.multi_selection);

  /**
   * Selector to get a channel by its ID.
   * @param id - The ID of the channel.
   * @returns A selector function.
   */
  static getChannelById(id: string) {
    return createSelector([TreeState], (state: TreeStateModel) => {
      return state.flatTree.find((n) => n.id === id) || ({} as DynamicFlatNode<any>);
    });
  }

  /**
   * Selector to get channels by their IDs.
   * @param ids - An array of channel IDs.
   * @returns A selector function.
   */
  static getChannelsByIds(ids: string[]) {
    return createSelector([TreeState], (state: TreeStateModel) => {
      return state.flatTree.filter((n) => ids.includes(n.id));
    });
  }

  /**
   * Selector to get channels of controls.
   * @param controls - An array of controls.
   * @returns A selector function.
   */
  static getChannelsOfControls(controls: Control[]) {
    return createSelector([TreeState], (state: TreeStateModel) => {
      return state.flatTree.filter((n) => controls.map((c) => c.id).includes(n.id));
    });
  }

  /**
   * Action to stop multi-selection.
   * @param ctx - The state context.
   */
  @Action(TreeStateActions.StopMultiSelection) stopMultiSelection(ctx: StateContext<TreeStateModel>) {
    const {
      multi_selection: { end, start },
    } = ctx.getState();

    if (end === undefined || start === undefined) {
      return ctx.setState(patch({ multi_selection: undefined }));
    }

    this.store.selectOnce(MultiViewState.getSensorTableControlsOfCurrentView).subscribe((existingControls) => {
      const selectedNodes = this.store.selectSnapshot(TreeState.getChannels).slice(start, end + 1);

      const controlsToAdd: Control[] = [];
      const controlsToRemove: Control[] = [];

      selectedNodes.forEach(async (node) => {
        const existingControl = existingControls.find(isSameControlAs(node));
        if (existingControl) {
          controlsToRemove.push(existingControl);
        } else {
          const control = node.toControl();
          controlsToAdd.push(control);
          try {
            await db.controls.put(control);
          } catch (e) {
            console.error('Error saving control to IndexedDB', e);
          }
        }
      });

      // Remove controls individually (keep existing behavior)
      controlsToRemove.forEach((control) => {
        ctx.dispatch(new RemoveControlFromCurrentView(control));
      });

      // Add controls in batch for improved performance
      if (controlsToAdd.length > 0) {
        ctx.dispatch(new AddBatchControlsToCurrentView(controlsToAdd));
      }
    });

    return ctx.setState(patch({ multi_selection: undefined }));
  }

  /**
   * Action to start multi-selection.
   * @param ctx - The state context.
   * @param action - The action to start multi-selection.
   */
  @Action(TreeStateActions.StartMultiSelection) startMultiSelection(ctx: StateContext<TreeStateModel>, action: TreeStateActions.StartMultiSelection) {
    const { multi_selection } = ctx.getState();
    if (multi_selection?.start !== undefined) {
      return ctx.dispatch(new TreeStateActions.EndMultiSelection(action.index));
    }

    return ctx.setState(patch({ multi_selection: { start: action.index } }));
  }

  /**
   * Action to end multi-selection.
   * @param ctx - The state context.
   * @param action - The action to end multi-selection.
   */
  @Action(TreeStateActions.EndMultiSelection) endMultiSelection(ctx: StateContext<TreeStateModel>, action: TreeStateActions.EndMultiSelection) {
    const { multi_selection } = ctx.getState();
    return ctx.setState(patch({ multi_selection: { ...multi_selection, end: action.index } }));
  }

  /**
   * Action to save the flat tree state.
   * @param ctx - The state context.
   * @param flatTree - The flat tree to save.
   */
  @Action(TreeStateActions.SaveFlatTree) saveTreeState(ctx: StateContext<TreeStateModel>, { flatTree }: TreeStateActions.SaveFlatTree) {
    ctx.setState(patch({ flatTree }));
  }

  /**
   * Action to clear nodes.
   * @param ctx - The state context.
   */
  @Action(TreeStateActions.ClearNodes) clearNodes(ctx: StateContext<TreeStateModel>) {
    ctx.setState(
      patch({
        flatTree: getDefaults().flatTree,
      })
    );
  }
  @Action(TreeStateActions.AddNodes) addNodes(ctx: StateContext<TreeStateModel>, { nodes, index: injectionIndex }: TreeStateActions.AddNodes) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        // First remove potenial dupilcate because on channel can only be in one group
        const ids = new Set(nodes.map((n) => n.id));
        draft.flatTree = draft.flatTree.filter(
          (n) => !ids.has(n.id) // remove the node itself
        );
        // Then add the new nodes
        draft.flatTree.splice(injectionIndex, 0, ...nodes);
      })
    );
  }
  /**
   * Action to toggle node visibility.
   * @param ctx - The state context.
   * @param opt - The options for toggling visibility.
   */
  @Action(TreeStateActions.ToggleNodeVisibility) toggleNodeVisibilty(
    ctx: StateContext<TreeStateModel>,
    { opt: { nodes, hide: hidden } }: TreeStateActions.ToggleNodeVisibility
  ) {
    if (!nodes) {
      return;
    }
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.flatTree = draft.flatTree.map((node) => {
          if (nodes.some((d) => d.id === node.id)) {
            return { ...node, hidden };
          }
          return node;
        });
      })
    );
  }
  @Action(TreeStateActions.DeleteNodes) deleteNodes(ctx: StateContext<TreeStateModel>, { nodes }: TreeStateActions.DeleteNodes) {
    const ids = new Set(nodes.map((n) => n.id));
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.flatTree = draft.flatTree.filter(
          (n) => !ids.has(n.id) // remove the node itself and its children
        );
      })
    );
  }

  /**
   * Action to hide all nodes.
   * @param ctx - The state context.
   */
  @Action(TreeStateActions.HideAllNodes) hideAllNodes(ctx: StateContext<TreeStateModel>) {
    return ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.flatTree = draft.flatTree.map((node) => {
          if (node.type !== TreeEntityType.USE_CASE_ROOT) {
            return { ...node, hidden: true };
          }
          return node;
        });
      })
    );
  }

  /**
   * Action to toggle tree loading state.
   * @param ctx - The state context.
   */
  @Action(TreeStateActions.ToggleTreeLoading) toggleTreeLoading(ctx: StateContext<TreeStateModel>) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.isLoading = !draft.isLoading;
      })
    );
  }

  /**
   * Action to set tree loading state to true.
   * @param ctx - The state context.
   */
  @Action(TreeStateActions.SetTreeLoading) setTreeLoading(ctx: StateContext<TreeStateModel>) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.isLoading = true;
      })
    );
  }

  /**
   * Action to set tree loading state to false.
   * @param ctx - The state context.
   */
  @Action(TreeStateActions.UnsetTreeLoading) unsetTreeLoading(ctx: StateContext<TreeStateModel>) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.isLoading = false;
      })
    );
  }

  /**
   * Action to toggle error state on a node.
   * @param ctx - The state context.
   * @param node - The node to toggle error state on.
   * @param isError - The error state to set.
   */
  @Action(TreeStateActions.ToggleErrorOnNode) toggleErrorOnNode(ctx: StateContext<TreeStateModel>, { node, isError }: TreeStateActions.ToggleErrorOnNode) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const index = draft.flatTree.findIndex(byId(node as any));
        if (index < 0) {
          return;
        }
        draft.flatTree[index] = {
          ...draft.flatTree[index],
          hasError: isError,
          isLoading: false,
        };
      })
    );
  }

  /**
   * Action to set the loading state on a specific node.
   * @param ctx - The state context.
   * @param node - The node to set loading state on.
   * @param isLoading - The loading state to set.
   */
  @Action(TreeStateActions.SetNodeLoading) setNodeLoading(ctx: StateContext<TreeStateModel>, { node, isLoading }: TreeStateActions.SetNodeLoading) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        const index = draft.flatTree.findIndex(byId(node as any));
        if (index < 0) {
          return;
        }
        draft.flatTree[index] = {
          ...draft.flatTree[index],
          isLoading,
        };
      })
    );
  }
}
